import React, { useEffect, useMemo } from 'react';
import CardCommon from '@op/components/CardCommon';
import { RightInfo } from '@op/components/TableExpandableDetail/RightInfo';
import { LanguageType } from '@op/types/common';
import { useRequestStats } from '@op/pages/feeds/hooks/useFeedsRequest';
import ReviewsModal from '@op/components/ReviewsModal';
import useReviewModalData from '@op/components/ReviewsModal/useReviewModalData';
import { transLanguage, transRegionText } from '@op/utils';
import { NIKKEGPTTips } from '@op/components/BubbleCommon';
import { Keyword, OverviewChart } from '@op/components/ExpandablePlane';
import { getNikkeHighGameGPTReport } from '@op/services';
import GPT, { getNIKKEPromptList } from '@op/components/GPT';
import { useIntl } from 'umi';
import moment from 'moment';
import { summaryGptTitle } from '../constants';

export default function NIKKEAnalysis({
  filter,
  language,
  hero,
  wholeGameInfo,
}: {
  filter: any;
  language: LanguageType;
  hero: string;
  wholeGameInfo: any;
}) {
  const { formatMessage } = useIntl();
  const { feedsStats, requestState } = useRequestStats({});
  const sentimentCountMap = useMemo(() => {
    const sentimentCountMap = {
      positive: feedsStats?.good_total || 0,
      neutral: feedsStats?.neutral_total || 0,
      negative: feedsStats?.bad_total || 0,
    };

    return sentimentCountMap;
  }, [feedsStats]);

  const params = useMemo(() => {
    return { ...filter, topic: [hero] };
  }, [filter]);

  useEffect(() => {
    requestState(params);
  }, [params]);

  const { reviewsVisible, data, openModal, toggleModal } = useReviewModalData();
  const promptList = getNIKKEPromptList({
    gameInfo: wholeGameInfo,
    topic: hero,
  });

  return (
    <CardCommon contentCard className="dbo-topic-analysis-card card-slot">
      <div className="dbo-topic-analysis">
        <div className="dbo-topic-analysis-left">
          <OverviewChart
            filter={params}
            type="language"
            title="Language"
            defaultChartType="line"
            translate={(text: string) => {
              return transLanguage(text);
            }}
          ></OverviewChart>
          <OverviewChart
            filter={params}
            type="country"
            defaultChartType="line"
            title="Region/Country"
            translate={(text: string) => {
              return transRegionText(text);
            }}
          ></OverviewChart>
          <OverviewChart
            filter={params}
            type="channel"
            defaultChartType="line"
            title="Channel"
          ></OverviewChart>
        </div>
        <div className="dbo-topic-analysis-right">
          <Keyword
            title="Keywords"
            detailKey="Nikke-keyword"
            language={language}
            filter={params}
          ></Keyword>

          <div className="dbo-topic-analysis-gpt">
            <RightInfo
              commonParams={params}
              sentimentCountMap={sentimentCountMap}
              language={language}
              hasGptPanel
              extInfo={{ nikke: hero }}
              itemClick={(item) => openModal(item)}
              sortItem="engagement"
              tips={NIKKEGPTTips}
            >
              <GPT.MultiplePageCard
                filter={params}
                hasStopBtn={true}
                contentCard
                oriFeedsState={feedsStats}
                requestFuncMap={{
                  defaultRequest: getNikkeHighGameGPTReport,
                  dealwithParams: ({
                    edition_unified_id,
                    end_time,
                    entity_type,
                    id_type,
                    start_time,
                    topic,
                    date_type,
                  }: any) => {
                    return {
                      edition_unified_id,
                      id_type,
                      entity_type,
                      date_type,
                      start_time: moment(start_time).format('YYYY-MM-DD'),
                      end_time: moment(end_time).format('YYYY-MM-DD'),
                      hero: topic[0],
                    };
                  },
                  dealwithData(data) {
                    let report_en = '';
                    let report_cn = '';
                    summaryGptTitle.map(({ key, keyEn, ...item }, index) => {
                      const value = data?.[key];
                      const valueEn = data?.[keyEn] || value;
                      if (value) {
                        report_en += `${index !== 0 ? '\n' : ''}### ${
                          item['en-US']
                        } \n${valueEn}`;
                        report_cn += `${index !== 0 ? '\n' : ''}### ${
                          item['zh-CN']
                        } \n${value}`;
                      }
                    });

                    return { report_cn, report_en };
                  },
                }}
                // saveName={"Nikke" + "_" + hero}
                title={formatMessage({ id: 'Summary' })}
                autoRequest={false}
                GPTList={summaryGptTitle}
                promptList={promptList}
              >
                {(toggleVisible, { loading, total }) => {
                  return (
                    <GPT.Button
                      extInfo={{ nikke: hero }}
                      disabled={total === 0}
                      onClick={() => toggleVisible()}
                    />
                  );
                }}
              </GPT.MultiplePageCard>
            </RightInfo>
          </div>
        </div>
      </div>
      <ReviewsModal
        visible={reviewsVisible}
        data={data}
        language={language}
        close={toggleModal}
      />
    </CardCommon>
  );
}
